import {createContext, FC, useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {WithChildren} from '../../../../_metronic/helpers'
import api, {registerProfileUpdateHandler, setApiCurrentUserId} from '../../../api'
import {AuthContextProps} from './_types'
import {getFingerprintId} from '../utils/fingerprint'
import {
    clearAuth,
    getAccessToken,
    hydrateAccessFromStorage,
    isAccessTokenExpired,
    setAccessToken,
    setAuthUserId,
} from '../utils/tokenStorage'
import {exchangeRefreshToAccess} from '../utils/authApi'
import {setAuthMode} from '../utils/authTransport'

const initAuthContextPropsState: AuthContextProps = {
    auth: undefined,
    saveAuth: () => {},
    currentUser: undefined,
    setCurrentUser: () => {},
    logout: () => {},
    fingerprint: undefined,
    setFingerprint: () => {},
    refreshUser: async () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
    return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
    const [auth, setAuth] = useState<string | undefined>(authHelper.getAuth())
    const [currentUser, setCurrentUser] = useState<UserModel | undefined>(undefined)
    const [fingerprint, setFingerprint] = useState<string | undefined>(undefined)
    const saveAuth = useCallback((token?: string) => {
        setAuth(token)
        if (token) authHelper.setAuth(token)
        else authHelper.removeAuth()
    }, [])

    const refreshUser = useCallback(async () => {
        try {
            const {data: user} = await api('users', 'get-current')
            setCurrentUser(user)
        } catch (e) {
            console.error('refreshUser failed', e)
        }
    }, [])

    const logout = useCallback(() => {
        setAuthMode('none')
        setAccessToken(undefined)
        setAuthUserId(undefined)
        setCurrentUser(undefined)
        clearAuth()
    }, [])

    const value = useMemo(
        () => ({
            auth,
            saveAuth,
            currentUser,
            setCurrentUser,
            logout,
            fingerprint,
            setFingerprint,
            refreshUser,
        }),
        [auth, saveAuth, currentUser, logout, fingerprint, refreshUser]
    )

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

const AuthInit: FC<WithChildren> = ({children}) => {
    const {currentUser, setCurrentUser, setFingerprint, refreshUser} = useAuth()
    const [showSplashScreen, setShowSplashScreen] = useState(true)

    useEffect(() => {
        let active = true
        ;(async () => {
            try {
                hydrateAccessFromStorage()

                const fp = await getFingerprintId()
                if (!active) return
                setFingerprint(fp)
                ;(window as any).__APP_FP__ = fp

                const tokenMissing = !getAccessToken()
                const tokenExpired = isAccessTokenExpired()

                if (tokenMissing || tokenExpired) {
                    try {
                        await exchangeRefreshToAccess()
                    } catch {}
                }
                const haveToken = !!getAccessToken()
                if (haveToken) {
                    setAuthMode('token')
                    try {
                        const {data: user} = await api('users', 'get-current')
                        if (active && user) setCurrentUser(user)
                    } catch (e: any) {
                        if (e?.__APP_NEED_RELOGIN__) {
                            setAuthMode('none')
                        } else {
                            console.error(e)
                        }
                    }
                } else {
                    setAuthMode('none')
                }
            } finally {
                if (active) setShowSplashScreen(false)
            }
        })()
        return () => {
            active = false
        }
    }, [])

    useEffect(() => {
        registerProfileUpdateHandler(refreshUser)
    }, [refreshUser])

    useEffect(() => {
        if (currentUser?.id) {
            setApiCurrentUserId(currentUser.id)
        }
    }, [currentUser])

    return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
